import React, { useContext, useState } from 'react';
import { checkTokenAndRenderCode } from '../utils/common-functions';
import {
  showSuccessMessage,
  showErrorMessage,
  showWarningMessage,
} from '../utils/toast-functions';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../reusable-components/AuthContext';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { StandardToastContainer } from '../reusable-components/StandardToastContainer';
import { useJobDetails } from '../hooks/use-job-details';
import { DownloadJobZipFileIcon } from '../icons/DownloadJobZipFileIcon';
import { DeleteIcon } from '../icons/DeleteIcon';
import { WarningIcon } from '../icons/WarningIcon';
import { ErrorIcon } from '../icons/ErrorIcon';
import { postDeleteContract } from '../services/job-api';
import { ITELLITY_BACKEND_BASE_URL } from '../services/constants';
import { DownloadJobLogsIcon } from '../icons/DownloadJobLogsIcon';

// Helper function to trim and handle empty strings
const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  const stringValue = String(value).trim();
  return stringValue === '' ? 'N/A' : stringValue;
};

export const JobDetailPage = () => {
  // constants
  const authContext = useContext(AuthContext);
  const { token } = authContext;

  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const [expandedContracts, setExpandedContracts] = useState<Set<string>>(
    new Set(),
  );
  const [allExpanded, setAllExpanded] = useState<boolean>(false);

  const { job, contracts, loading, error, refreshData } = useJobDetails(
    token,
    jobId,
  );

  const handleDownloadZipFile = () => {
    if (job && job.jobState.toUpperCase() === 'DONE') {
      const url = `${ITELLITY_BACKEND_BASE_URL}/output/${job.userId}-${job.customerId}/${job.zipFileName}`;
      window.open(url, '_blank');
    } else {
      showWarningMessage('Sorry, the job zip file is not available.');
    }
  };

  const handleBrowseLogFile = async () => {
    if (job) {
      const url = `${ITELLITY_BACKEND_BASE_URL}/output/${job.userId}-${job.customerId}/job_${job.jobId}.log`;
      window.open(url, '_blank');
    } else {
      showWarningMessage('Sorry, the job log file is not available.');
    }
  };

  const handleDeleteContract = async (contractPk: string) => {
    if (!jobId || !token || !contractPk) return;

    try {
      const response = await postDeleteContract(token, contractPk, jobId);
      if (response.success) {
        showSuccessMessage('Contract deleted successfully.');
        // Refresh the data to update the UI
        refreshData();
      } else {
        showErrorMessage(response.userMessage || 'Failed to delete contract.');
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      showErrorMessage('An error occurred while deleting the contract.');
    }
  };

  const toggleContract = (phoneNumber: string) => {
    setExpandedContracts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(phoneNumber)) {
        newSet.delete(phoneNumber);
      } else {
        newSet.add(phoneNumber);
      }

      // Update allExpanded state based on whether all contracts are now expanded
      if (contracts && newSet.size === contracts.length) {
        setAllExpanded(true);
      } else if (newSet.size === 0) {
        setAllExpanded(false);
      }

      return newSet;
    });
  };

  const toggleAllContracts = () => {
    if (allExpanded) {
      // Close all contracts
      setExpandedContracts(new Set());
      setAllExpanded(false);
    } else {
      // Open all contracts
      const allPhoneNumbers = new Set(
        contracts?.map((c) => c.phoneNumber) || [],
      );
      setExpandedContracts(allPhoneNumbers);
      setAllExpanded(true);
    }
  };

  const pageJSX: JSX.Element = (
    <div className="md:container md:mx-auto px-10">
      <StandardToastContainer />
      <h1 className="text-2xl font-bold my-4">Job Details</h1>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <span
            className="loading loading-spinner loading-lg"
            data-testid="loading-spinner"
          ></span>
        </div>
      ) : error ? (
        <div className="alert alert-error shadow-lg">
          <div>
            <ErrorIcon />
            <span>{error}</span>
          </div>
        </div>
      ) : job ? (
        <div className="bg-base-100 shadow-md rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Job Information</h2>
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <tbody>
                    <tr>
                      <td className="font-semibold">Job ID</td>
                      <td>{job.jobId}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">Execution Date</td>
                      <td>{job.executionDate}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">Status</td>
                      <td>
                        <span
                          className={`badge ${getJobStateBadgeColor(job.jobState)}`}
                        >
                          {job.jobState}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="font-semibold">ZIP File Name</td>
                      <td>{job.zipFileName}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">Contract Amount</td>
                      <td>{job.contractAmount}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">Input Path</td>
                      <td>{job.inputPath}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">XML Value</td>
                      <td>
                        <div className="max-h-40 overflow-auto">
                          <pre className="text-xs whitespace-pre-wrap">
                            {job.xmlValue}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4">
                Customer Information
              </h2>
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <tbody>
                    <tr>
                      <td className="font-semibold">Customer Name</td>
                      <td>{formatValue(job.customerName)}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">Customer ID</td>
                      <td>{formatValue(job.customerId)}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold">User ID</td>
                      <td>{formatValue(job.userId)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-8">
                <button
                  className="btn btn-primary flex items-center gap-2"
                  onClick={handleDownloadZipFile}
                >
                  <DownloadJobZipFileIcon /> Download ZIP File
                </button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <button
                  className="btn btn-secondary flex items-center gap-2"
                  onClick={handleBrowseLogFile}
                >
                  <DownloadJobLogsIcon /> Browse Log File
                </button>
              </div>
            </div>
          </div>

          {contracts && contracts.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Contract Details</h2>
              <div className="overflow-x-auto">
                <div>
                  <div className="mb-4">
                    <button
                      className="btn btn-outline"
                      onClick={toggleAllContracts}
                    >
                      {allExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                  </div>
                  {contracts.map((contract, index) => (
                    <div
                      className="mb-4 border rounded-lg shadow-sm"
                      key={contract.contractPk || index}
                    >
                      <div
                        className={`p-4 font-semibold flex justify-between items-center ${
                          expandedContracts.has(contract.phoneNumber)
                            ? 'bg-base-300'
                            : 'bg-base-200'
                        } cursor-pointer`}
                        onClick={() => toggleContract(contract.phoneNumber)}
                      >
                        <span className="font-medium">
                          <span className="font-bold">Contract:</span>{' '}
                          {formatValue(contract.phoneNumber)}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            className="btn btn-outline btn-error btn-sm bg-transparent"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteContract(contract.contractPk);
                            }}
                            title="Delete contract"
                          >
                            <DeleteIcon />
                          </button>
                          <span className="text-xl">
                            {expandedContracts.has(contract.phoneNumber)
                              ? '−'
                              : '+'}
                          </span>
                        </div>
                      </div>
                      {expandedContracts.has(contract.phoneNumber) && (
                        <div className="p-4 bg-base-100">
                          <table className="table table-zebra w-full">
                            <tbody>
                              <tr>
                                <td className="font-semibold w-1/3">
                                  Contract ID
                                </td>
                                <td>{formatValue(contract.contractPk)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Phone Number</td>
                                <td>{formatValue(contract.phoneNumber)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Old Plan</td>
                                <td>{formatValue(contract.oldPlan)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">New Plan</td>
                                <td>{formatValue(contract.newPlan)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Total Old</td>
                                <td>{formatValue(contract.totalOld)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Total New</td>
                                <td>{formatValue(contract.totalNew)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Savings</td>
                                <td>{formatValue(contract.savings)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">
                                  Selected Plan ID
                                </td>
                                <td>{formatValue(contract.selectedPlanId)}</td>
                              </tr>
                              <tr>
                                <td className="font-semibold">Actions</td>
                                <td>
                                  <button
                                    className="btn btn-outline btn-error btn-sm bg-transparent flex items-center gap-2"
                                    onClick={() =>
                                      handleDeleteContract(contract.contractPk)
                                    }
                                  >
                                    <DeleteIcon /> Delete
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/rating-jobs')}
            >
              Back to Jobs List
            </button>
          </div>
        </div>
      ) : (
        <div className="alert alert-warning shadow-lg">
          <div>
            <WarningIcon />
            <span>No job found with ID: {jobId}</span>
          </div>
        </div>
      )}
    </div>
  );

  return checkTokenAndRenderCode(token, pageJSX, setCurrentRoute, navigate);
};

function getJobStateBadgeColor(jobState: string): string {
  switch (jobState.toUpperCase()) {
    case 'DONE':
      return 'badge-success';
    case 'STARTED':
      return 'badge-info';
    case 'PROG':
      return 'badge-warning';
    case 'ERROR':
      return 'badge-error';
    default:
      return 'badge-ghost';
  }
}
